import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, Icon, Typography, useDisableGlobalBanner, type IIconsVector } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { AuthPath, ConversationRoutes } from 'entities/ExportRouters'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useRedirectRouter } from '../hooks/useRedirectRouter'
import styles from './styles.module.scss'

type IPageNotFoundProps = {
  redirectPath?: string
  description?: string
  buttonText?: string
  buttonIcon?: IIconsVector | null
}

export const PAGE_NOT_FOUND_GO_BACK_PROPS: IPageNotFoundProps = {
  description: 'Check the address or go back.',
  buttonText: 'Go back',
  buttonIcon: null,
}

export const PageNotFound = observer(
  ({ redirectPath, description, buttonText, buttonIcon }: IPageNotFoundProps) => {
    useRedirectRouter()
    useDisableGlobalBanner()

    const { authStore } = useEntitiesContext()
    const isLoggedIn = authStore.isLoggedIn

    useEffect(() => {
      const previousTitle = document.title
      document.title = '404 — Page Not Found | Salesmsg'

      return () => {
        document.title = previousTitle
      }
    }, [])

    const targetPath =
      redirectPath ?? (isLoggedIn ? `/${ConversationRoutes.root}` : `/${AuthPath.login}`)
    const descriptionText =
      description ??
      (isLoggedIn
        ? 'Check the address or go back to your conversations.'
        : "Sign in to find what you're looking for.")
    const actionText = buttonText ?? (isLoggedIn ? 'Go to conversations' : 'Sign in')
    const defaultActionIcon: IIconsVector | undefined = isLoggedIn ? 'conversation' : undefined
    const actionIcon = buttonIcon === null ? undefined : buttonIcon ?? defaultActionIcon

    return (
      <div className={styles.wrap}>
        <div className={styles.content}>
          <div className={styles.iconCircle}>
            <div className={styles.iconBox}>
              <Icon
                icon={'failed'}
                fontSize={20}
                color={'var(--content-primary-primary-inverted)'}
                ariaLabel={'PageNotFound_icon'}
              />
            </div>
          </div>

          <Typography
            className={styles.title}
            variant={'heading-lg'}
            ariaLabel={'PageNotFound_title'}
          >
            Page not found
          </Typography>

          <Typography
            className={styles.description}
            variant={'body-lg-regular'}
            color={'var(--content-primary-tertiary)'}
            tag={'div'}
            ariaLabel={'PageNotFound_description'}
          >
            That link isn&apos;t valid.
            <br />
            {descriptionText}
          </Typography>

          <Button
            size={'medium'}
            icon={actionIcon}
            text={actionText}
            onClick={() => uiStore.changeRoute({ path: targetPath })}
            aria-label={'PageNotFoundAction'}
          />
        </div>
      </div>
    )
  }
)
